import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { Shield, User, Trash2, Crown, RefreshCw, Check, X } from 'lucide-react'
import { usersAPI } from '../../lib/api'
import { LoadingSpinner } from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface UserData {
  id: string
  email: string
  role: string
  created_at: string
  last_sign_in_at?: string
  full_name?: string
  is_super_user: boolean
}

export function UserManagement() {
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [selectedRole, setSelectedRole] = useState<Record<string, string>>({})
  const queryClient = useQueryClient()

  // Fetch users
  const { data, isLoading } = useQuery(
    'users',
    () => usersAPI.getUsers(),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  )

  // Update role mutation
  const updateRole = useMutation(
    ({ userId, role }: { userId: string; role: string }) =>
      usersAPI.updateUserRole(userId, { role }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        toast.success('Benutzerrolle erfolgreich aktualisiert')
        setEditingUserId(null)
      },
      onError: (error: any) => {
        toast.error('Fehler beim Aktualisieren: ' + (error.response?.data?.detail || 'Unbekannter Fehler'))
      }
    }
  )

  // Delete user mutation
  const deleteUser = useMutation(
    (userId: string) => usersAPI.deleteUser(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('users')
        toast.success('Benutzer erfolgreich gelöscht')
      },
      onError: (error: any) => {
        toast.error('Fehler beim Löschen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'))
      }
    }
  )

  const handleEditRole = (userId: string, currentRole: string) => {
    setEditingUserId(userId)
    setSelectedRole(prev => ({ ...prev, [userId]: currentRole }))
  }

  const handleSaveRole = (userId: string) => {
    const newRole = selectedRole[userId]
    if (newRole) {
      updateRole.mutate({ userId, role: newRole })
    }
  }

  const handleCancelEdit = () => {
    setEditingUserId(null)
    setSelectedRole({})
  }

  const handleDeleteUser = (userId: string, email: string) => {
    if (confirm(`Möchten Sie den Benutzer ${email} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      deleteUser.mutate(userId)
    }
  }

  if (isLoading) {
    return (
      <div className="glass rounded-xl p-8 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  const users = data?.users || []
  const total = data?.total || 0

  return (
    <div className="space-y-6">
      <div className="glass rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Benutzerverwaltung</h2>
            <p className="text-gray-400">
              Verwalten Sie Benutzer und deren Rollen im System. 
              Insgesamt {total} {total === 1 ? 'Benutzer' : 'Benutzer'} registriert.
            </p>
          </div>
          <button
            onClick={() => queryClient.invalidateQueries('users')}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Aktualisieren</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-400">Benutzer</th>
                <th className="text-left py-3 px-4 font-medium text-gray-400">Rolle</th>
                <th className="text-left py-3 px-4 font-medium text-gray-400">Registriert</th>
                <th className="text-left py-3 px-4 font-medium text-gray-400">Letzter Login</th>
                <th className="text-right py-3 px-4 font-medium text-gray-400">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user: UserData) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="border-b border-gray-800 hover:bg-white/5 transition-colors"
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-800 rounded-lg">
                        {user.is_super_user ? (
                          <Crown className="w-5 h-5 text-yellow-500" />
                        ) : user.role === 'admin' ? (
                          <Shield className="w-5 h-5 text-primary-500" />
                        ) : (
                          <User className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{user.email}</p>
                        {user.full_name && (
                          <p className="text-sm text-gray-400">{user.full_name}</p>
                        )}
                        {user.is_super_user && (
                          <p className="text-xs text-yellow-500 font-medium">Super User</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {editingUserId === user.id && !user.is_super_user ? (
                      <select
                        value={selectedRole[user.id] || user.role}
                        onChange={(e) => setSelectedRole(prev => ({
                          ...prev,
                          [user.id]: e.target.value
                        }))}
                        className="px-3 py-1 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-primary-500"
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        user.role === 'admin' 
                          ? 'bg-primary-600/20 text-primary-400' 
                          : 'bg-gray-700 text-gray-300'
                      }`}>
                        {user.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-400">
                    {new Date(user.created_at).toLocaleDateString('de-DE')}
                  </td>
                  <td className="py-4 px-4 text-sm text-gray-400">
                    {user.last_sign_in_at 
                      ? new Date(user.last_sign_in_at).toLocaleDateString('de-DE')
                      : 'Noch nie'
                    }
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end space-x-2">
                      {editingUserId === user.id ? (
                        <>
                          <button
                            onClick={() => handleSaveRole(user.id)}
                            disabled={updateRole.isLoading}
                            className="p-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateRole.isLoading}
                            className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          {!user.is_super_user && (
                            <button
                              onClick={() => handleEditRole(user.id, user.role)}
                              className="btn btn-secondary btn-sm"
                            >
                              Rolle ändern
                            </button>
                          )}
                          {!user.is_super_user && (
                            <button
                              onClick={() => handleDeleteUser(user.id, user.email)}
                              className="p-2 hover:bg-red-600/20 rounded-lg transition-colors text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 p-4 bg-blue-600/20 border border-blue-600/30 rounded-lg">
          <h3 className="font-medium text-blue-300 mb-2">Rollenübersicht:</h3>
          <ul className="space-y-2 text-sm text-blue-200">
            <li>• <strong>User:</strong> Kann das Chat-System nutzen und Wissenslücken melden</li>
            <li>• <strong>Admin:</strong> Hat zusätzlich Zugriff auf das Management Portal</li>
            <li>• <strong>Super User:</strong> Unveränderlicher Administrator (marco.heer@pflegehilfe-senioren.de)</li>
          </ul>
        </div>
      </div>
    </div>
  )
}